/**
 * store-stats.js
 * Lit les vraies données de la boutique sélectionnée dans Firebase
 * (collection "Sites", "Commandes" et sous-collection "Products")
 * et calcule les statistiques affichées sur le tableau de bord.
 */
import firebase from '@/utils/firestore-shim';
import Cookies from 'js-cookie';

export const LOW_STOCK_THRESHOLD = 5;
export const MONTHS_WINDOW = 12;

const ORDER_STATUSES = ['Processing', 'Shipped', 'Delivered', 'Cancelled'];

/* ------------------------------ Cookie store ------------------------------ */

export function getSelectedHost() {
  return Cookies.get('hostCookie') || null;
}

export function setSelectedHost(host) {
  if (host) {
    Cookies.set('hostCookie', host);
  } else {
    Cookies.remove('hostCookie');
  }
}

/* --------------------------------- Helpers -------------------------------- */

/** Convertit une valeur Firestore en nombre ("1 234,56", "1,234.56", ...). */
export function parseNumber(value) {
  if (typeof value === 'number') {
    return isFinite(value) ? value : 0;
  }
  if (value === null || value === undefined) {
    return 0;
  }
  let str = String(value).trim();
  if (!str) {
    return 0;
  }
  str = str.replace(/[^\d.,-]/g, '');
  const lastComma = str.lastIndexOf(',');
  const lastDot = str.lastIndexOf('.');
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      str = str.replace(/\./g, '').replace(',', '.');
    } else {
      str = str.replace(/,/g, '');
    }
  } else if (lastComma > -1) {
    const decimals = str.length - lastComma - 1;
    const commaCount = (str.match(/,/g) || []).length;
    if (commaCount > 1 || decimals === 3) {
      str = str.replace(/,/g, '');
    } else {
      str = str.replace(',', '.');
    }
  }
  const num = parseFloat(str);
  return isFinite(num) ? num : 0;
}

/** Convertit une date Firestore / texte localisé en objet Date. */
export function parseOrderDate(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      try {
        const d = value.toDate();
        return isNaN(d.getTime()) ? null : d;
      } catch (e) {
        return null;
      }
    }
    if (typeof value.seconds === 'number') {
      const d = new Date(value.seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const str = String(value).trim();
  if (!str) {
    return null;
  }
  const iso = str.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})/);
  if (iso) {
    const d = new Date(+iso[1], +iso[2] - 1, +iso[3]);
    return isNaN(d.getTime()) ? null : d;
  }
  const direct = new Date(str);
  if (!isNaN(direct.getTime())) {
    return direct;
  }
  const parts = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
  if (parts) {
    let a = +parts[1];
    let b = +parts[2];
    let year = +parts[3];
    if (year < 100) {
      year += 2000;
    }
    let day = b;
    let month = a;
    if (a > 12) {
      day = a;
      month = b;
    }
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function normalizeStatus(value) {
  const raw = String(value || '').trim();
  const found = ORDER_STATUSES.find((s) => s.toLowerCase() === raw.toLowerCase());
  return found || (raw || 'Processing');
}

export function isCancelled(order) {
  return normalizeStatus(order && order.status) === 'Cancelled';
}

export function fullName(order) {
  const u = (order && order.userDetail) || {};
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name || (u.email || '').trim();
}

function monthIndex(date) {
  return date.getFullYear() * 12 + date.getMonth();
}

function appLanguage() {
  if (typeof localStorage === 'undefined') {
    return undefined;
  }
  const stored = localStorage.getItem('currentLanguage');
  return stored || undefined;
}

/** Formate un montant avec le symbole de la boutique. */
export function formatMoney(value, symbol) {
  const num = parseNumber(value);
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return symbol ? `${symbol} ${formatted}` : formatted;
}

/** Formate un nombre sans décimale. */
export function formatNumber(value) {
  return parseNumber(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Formate une date (objet Date ou texte brut). */
export function formatDate(date, fallback) {
  if (!date) {
    return fallback || '';
  }
  return date.toLocaleString(appLanguage(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Vrai si la date tombe dans la plage [from, to] (bornes « AAAA-MM-JJ »
 * d'un <input type="date">, incluses). Sans aucune borne, tout passe ; une
 * valeur sans date lisible est exclue dès qu'une borne est posée.
 */
export function inDateRange(value, from, to) {
  if (!from && !to) {
    return true;
  }
  const date = parseOrderDate(value);
  if (!date) {
    return false;
  }
  if (from) {
    const min = new Date(from + 'T00:00:00');
    if (!isNaN(min.getTime()) && date.getTime() < min.getTime()) {
      return false;
    }
  }
  if (to) {
    const max = new Date(to + 'T23:59:59.999');
    if (!isNaN(max.getTime()) && date.getTime() > max.getTime()) {
      return false;
    }
  }
  return true;
}

/* ------------------------------ Firebase reads ----------------------------- */

/**
 * Lit les commandes payées, paniers abandonnés et produits d'une boutique.
 */
async function fetchStoreCollections(host, storeDocId, store) {
  const db = firebase.firestore();
  const sites = [host, 'www.' + host];

  const [ordersSnap, cartsSnap, productsSnap] = await Promise.all([
    db
      .collection('Commandes')
      .where('mode_payment', '!=', '')
      .where('site', 'in', sites)
      .get(),
    db
      .collection('Commandes')
      .where('mode_payment', '==', '')
      .where('site', 'in', sites)
      .get(),
    storeDocId
      ? db.collection('Sites').doc(storeDocId).collection('Products').get()
      : Promise.resolve(null)
  ]);

  const orders = ordersSnap.docs.map((doc) => {
    const o = doc.data();
    o.document = doc.id;
    return o;
  });
  const abandoned = cartsSnap.docs.map((doc) => {
    const o = doc.data();
    o.document = doc.id;
    return o;
  });
  const products = [];
  if (productsSnap) {
    productsSnap.forEach((doc) => {
      const p = doc.data();
      if (!p.id) {
        p.id = doc.id;
      }
      products.push(p);
    });
  }

  return { host, store, orders, abandoned, products };
}

/**
 * Récupère la boutique sélectionnée et toutes ses données réelles :
 * commandes payées, paniers abandonnés et produits.
 */
export async function fetchStoreData(host) {
  const db = firebase.firestore();
  let store = null;
  let storeDocId = null;
  const sitesSnap = await db
    .collection('Sites')
    .where('host', '==', host)
    .limit(1)
    .get();
  if (!sitesSnap.empty) {
    storeDocId = sitesSnap.docs[0].id;
    store = sitesSnap.docs[0].data();
    store.id = storeDocId;
  }
  return fetchStoreCollections(host, storeDocId, store);
}

/**
 * Récupère les données réelles de toutes les boutiques (vue globale).
 */
export async function fetchAllStoresData() {
  const db = firebase.firestore();
  const sitesSnap = await db.collection('Sites').get();
  const stores = sitesSnap.docs.map((doc) => {
    const store = doc.data();
    store.id = doc.id;
    if (!store.host) {
      store.host = doc.id;
    }
    return store;
  });
  return Promise.all(
    stores.map((store) => fetchStoreCollections(store.host, store.id, store))
  );
}

/* ------------------------------- Calculs stats ----------------------------- */

/** Construit la série mensuelle (12 mois) à partir des commandes datées. */
function buildMonthly(dated, endMonth) {
  const buckets = [];
  const bucketByKey = {};
  for (let i = MONTHS_WINDOW - 1; i >= 0; i--) {
    const date = new Date(endMonth.getFullYear(), endMonth.getMonth() - i, 1);
    const bucket = { key: monthIndex(date), date, revenue: 0, orders: 0 };
    buckets.push(bucket);
    bucketByKey[bucket.key] = bucket;
  }
  dated.forEach((item) => {
    if (!item.date) {
      return;
    }
    const bucket = bucketByKey[monthIndex(item.date)];
    if (bucket) {
      bucket.revenue += parseNumber(item.order.amt);
      bucket.orders += 1;
    }
  });
  const monthly = { labels: [], revenue: [], orders: [] };
  buckets.forEach((bucket) => {
    monthly.labels.push(
      bucket.date.toLocaleDateString(appLanguage(), { month: 'short', year: '2-digit' })
    );
    monthly.revenue.push(Math.round(bucket.revenue * 100) / 100);
    monthly.orders.push(bucket.orders);
  });
  return monthly;
}

/** Dernier mois de la fenêtre : mois courant, ou mois de la dernière commande si toutes sont plus anciennes. */
function windowEndMonth(dated) {
  const now = new Date();
  let latestDate = null;
  dated.forEach((item) => {
    if (item.date && (!latestDate || item.date > latestDate)) {
      latestDate = item.date;
    }
  });
  let endMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  if (latestDate && monthIndex(endMonth) - monthIndex(latestDate) > MONTHS_WINDOW - 1) {
    endMonth = new Date(latestDate.getFullYear(), latestDate.getMonth(), 1);
  }
  return endMonth;
}

/**
 * Calcule toutes les statistiques du tableau de bord à partir des données réelles.
 */
export function computeStats({ store, orders, abandoned, products }) {
  const safeOrders = orders || [];
  const safeProducts = products || [];
  const activeOrders = safeOrders.filter((o) => !isCancelled(o));

  const revenue = activeOrders.reduce((sum, o) => sum + parseNumber(o.amt), 0);
  const orderCount = activeOrders.length;
  const avgBasket = orderCount ? revenue / orderCount : 0;

  const enabledProducts = safeProducts.filter((p) => p.status !== false);
  const outOfStock = enabledProducts.filter((p) => parseNumber(p.stock) <= 0);
  const lowStock = enabledProducts.filter(
    (p) => parseNumber(p.stock) <= LOW_STOCK_THRESHOLD
  );

  /* Ventes par mois : fenêtre de 12 mois ancrée sur le mois courant
     (ou sur le mois de la dernière commande si toutes sont plus anciennes). */
  const now = new Date();
  const dated = activeOrders.map((o) => ({ order: o, date: parseOrderDate(o.orderDate) }));
  const monthly = buildMonthly(dated, windowEndMonth(dated));

  const currentKey = monthIndex(now);
  const revenueMonth = dated.reduce(
    (sum, item) => (item.date && monthIndex(item.date) === currentKey ? sum + parseNumber(item.order.amt) : sum),
    0
  );
  const ordersMonth = dated.filter((item) => item.date && monthIndex(item.date) === currentKey).length;

  /* Répartition par statut (toutes commandes payées, y compris annulées). */
  const statusCounts = { Processing: 0, Shipped: 0, Delivered: 0, Cancelled: 0 };
  safeOrders.forEach((o) => {
    const key = normalizeStatus(o.status);
    statusCounts[key] = (statusCounts[key] || 0) + 1;
  });

  /* Dernières commandes (tri par date décroissante). */
  const sortedOrders = safeOrders.slice().sort((a, b) => {
    const da = parseOrderDate(a.orderDate);
    const db = parseOrderDate(b.orderDate);
    return (db ? db.getTime() : 0) - (da ? da.getTime() : 0);
  });
  const latestOrders = sortedOrders.slice(0, 15).map((o) => ({
    document: o.document,
    transactionId: o.transactionId || o.document,
    date: parseOrderDate(o.orderDate),
    dateRaw: o.orderDate,
    customer: fullName(o),
    email: (o.userDetail && o.userDetail.email) || '',
    payment: o.mode_payment || '',
    status: normalizeStatus(o.status),
    amt: parseNumber(o.amt)
  }));

  /* Top produits (quantités et revenus réels des commandes). */
  const productMap = {};
  activeOrders.forEach((o) => {
    (o.products || []).forEach((p) => {
      const key = p.id || p.title || 'unknown';
      if (!productMap[key]) {
        productMap[key] = {
          title: p.title || key,
          image: p.image || p.FirstImage || '',
          qty: 0,
          revenue: 0
        };
      }
      const qty = parseNumber(p.quantity) || 1;
      const price = parseNumber(p.price);
      productMap[key].qty += qty;
      productMap[key].revenue += price * qty;
      if (!productMap[key].image && (p.image || p.FirstImage)) {
        productMap[key].image = p.image || p.FirstImage;
      }
    });
  });
  const topProducts = Object.values(productMap)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5);
  topProducts.forEach((p) => {
    p.share = revenue ? (p.revenue / revenue) * 100 : 0;
  });

  /* Top clients. */
  const customerMap = {};
  activeOrders.forEach((o) => {
    const u = o.userDetail || {};
    const key =
      (u.email || '').trim().toLowerCase() || fullName(o) || o.document;
    if (!customerMap[key]) {
      customerMap[key] = { name: fullName(o), email: u.email || '', count: 0, spent: 0 };
    }
    customerMap[key].count += 1;
    customerMap[key].spent += parseNumber(o.amt);
  });
  const topCustomers = Object.values(customerMap)
    .sort((a, b) => b.spent - a.spent)
    .slice(0, 5);

  const kpis = {
    revenue,
    revenueMonth,
    orderCount,
    ordersMonth,
    avgBasket,
    cancelled: safeOrders.length - activeOrders.length,
    lowStock: lowStock.length,
    outOfStock: outOfStock.length,
    productsCount: safeProducts.length,
    enabledProductsCount: enabledProducts.length,
    customersCount: Object.keys(customerMap).length
  };

  const todo = {
    toPrepare: statusCounts.Processing || 0,
    inTransit: statusCounts.Shipped || 0,
    abandoned: (abandoned || []).length,
    lowStock: lowStock.length,
    outOfStock: outOfStock.length
  };

  const currencySymbol =
    (store && store.symbol) ||
    (safeOrders[0] && safeOrders[0].symbol) ||
    '';

  return {
    currencySymbol,
    kpis,
    monthly,
    statusCounts,
    todo,
    latestOrders,
    topProducts,
    topCustomers
  };
}

/* ------------------------------ Vue globale ------------------------------- */

/**
 * Agrège les statistiques de toutes les boutiques (page d'accueil globale).
 */
export function computeGlobalStats(dataList) {
  const list = dataList || [];
  const perStore = list.map((data) => ({ data, stats: computeStats(data) }));

  const totals = {
    revenue: 0,
    revenueMonth: 0,
    orderCount: 0,
    ordersMonth: 0,
    avgBasket: 0,
    cancelled: 0,
    abandoned: 0,
    lowStock: 0,
    outOfStock: 0,
    productsCount: 0,
    customersCount: 0
  };
  const allDated = [];
  perStore.forEach(({ data, stats }) => {
    const k = stats.kpis;
    totals.revenue += k.revenue;
    totals.revenueMonth += k.revenueMonth;
    totals.orderCount += k.orderCount;
    totals.ordersMonth += k.ordersMonth;
    totals.cancelled += k.cancelled;
    totals.abandoned += (data.abandoned || []).length;
    totals.lowStock += k.lowStock;
    totals.outOfStock += k.outOfStock;
    totals.productsCount += k.productsCount;
    totals.customersCount += k.customersCount;
    (data.orders || []).forEach((o) => {
      if (!isCancelled(o)) {
        allDated.push({ order: o, date: parseOrderDate(o.orderDate) });
      }
    });
  });
  totals.avgBasket = totals.orderCount ? totals.revenue / totals.orderCount : 0;

  const monthly = buildMonthly(allDated, windowEndMonth(allDated));

  const symbols = [];
  perStore.forEach(({ data }) => {
    const symbol =
      (data.store && data.store.symbol) || (data.orders[0] && data.orders[0].symbol);
    if (symbol && symbols.indexOf(symbol) === -1) {
      symbols.push(symbol);
    }
  });

  const stores = perStore
    .map(({ data, stats }) => ({
      host: data.host,
      title: (data.store && data.store.title) || data.host,
      symbol:
        (data.store && data.store.symbol) ||
        (data.orders[0] && data.orders[0].symbol) ||
        '',
      revenue: stats.kpis.revenue,
      revenueMonth: stats.kpis.revenueMonth,
      orders: stats.kpis.orderCount,
      abandoned: (data.abandoned || []).length,
      lowStock: stats.kpis.lowStock,
      share: totals.revenue ? (stats.kpis.revenue / totals.revenue) * 100 : 0
    }))
    .sort((a, b) => b.revenue - a.revenue);

  return {
    currencySymbol: symbols.length === 1 ? symbols[0] : '',
    kpis: totals,
    monthly,
    stores
  };
}

/* --------------------------------- Export CSV ------------------------------ */

/** Télécharge un tableau d'objets au format CSV (compatible Excel). */
export function downloadCSV(rows, filename) {
  if (!rows || !rows.length) {
    return;
  }
  const escape = (value) => {
    const str = value === null || value === undefined ? '' : String(value);
    return /[",\n;]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
  };
  const headers = Object.keys(rows[0]);
  const lines = [headers.join(',')].concat(
    rows.map((row) => headers.map((h) => escape(row[h])).join(','))
  );
  const blob = new Blob(['\ufeff' + lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
